import { defineStore, storeToRefs } from "pinia";
import { BlueprintResource } from "@/editor/blueprints/BlueprintResource";
import { Tileset } from "@/client/communication/api/blueprints/Tileset";
import { AdminToSystemEvent } from "@/client/communication/api/bindings/AdminToSystemEvent";
import { computed, reactive, toRefs, watch } from "vue";
import {
  Node,
  RenderGraphData,
  use_game_instances_store,
} from "@/editor/stores/game-instances";
import {
  create_game_node2d,
  new_render_sprite,
  use_resources_store,
} from "@/editor/stores/resources";
import { Entity } from "@/editor/blueprints/Entity";
import { GameNodeKind } from "@/editor/blueprints/GameNodeKind";
import { LayerKind } from "@/editor/blueprints/LayerKind";
import { TerrainBrush } from "@/editor/blueprints/TerrainBrush";
import {
  GameNodeIdentifier,
  Isometry,
  RayCastNode,
  SelectMouseEvent,
} from "@/client/renderer";
import { use_inspector_store } from "@/editor/stores/inspector";
import { get_simulation_scale } from "@/client/config/config";
import { EntityUpdateKind } from "@/editor/blueprints/EntityUpdateKind";
import { Tile } from "@/editor/blueprints/Tile";
import { ColliderShape } from "@/editor/blueprints/ColliderShape";
import { match, P } from "ts-pattern";
import { AudioSet } from "@/editor/blueprints/AudioSet";

export type Point = { y: number; x: number };

export type InspectorComponent =
  | "module"
  | "tile"
  | "placement"
  | "map"
  | "game_node"
  | "character_animation_state"
  | "nothing";

export type EditMode = "select" | "placing" | "remove" | "paint";

export interface EditorStore {
  editor_open: boolean;
  selected_module_id: string;
  current_main_instance_id: string;
  current_map_path: string;
  edit_module_id: string;
  tile_brush: number[][];
  terrain_brush: TerrainBrush | null;
  terrain_brush_size: number;
  current_map_index: number;
  module_instance_map: { [module_id: string]: string[] };
  selected_nav_top_tab: "current" | "modules" | "resources" | "settings";
  sync_with_main_scene: boolean;
  selected_resource_tab: number;
  open_resource_paths: string[];
  selected_tileset_path: string;
  selected_tile_id: number;
  selected_tile_position: Point;
  selected_tile_layer: LayerKind;
  edit_mode: EditMode;
  selection_data: {
    node: RayCastNode | null;
    node_position: Point | null;
  };
  client_connection_ready: boolean;
  selected_scene_props: {
    scene_path: string | null;
    transparency: number;
    is_pinned: boolean;
  };
  inspecting_worlds: {
    main?: {
      module_id: string;
      instance_id: string;
      world_id: string;
      map_resource_path: string;
    };
  };
  current_main_instance: { instance_id?: string; world_id?: string };
  active_component: InspectorComponent;
}

export const use_editor_store = defineStore(
  "editor",
  () => {
    const state: EditorStore = reactive({
      inspecting_worlds: {},
      active_component: "nothing",
      edit_mode: "paint",
      editor_open: false,
      module_instance_map: {},
      sync_with_main_scene: false,
      selection_data: {
        node: null,
        node_position: null,
      },
      place_data: {
        node_to_place_in: null,
        place_type: "background",
        layer: "BG00",
      },
      tile_brush: [[0]],
      terrain_brush: null,
      terrain_brush_size: 1,
      selected_module_id: "",
      selected_tileset_path: "",
      selected_tile_id: 0,
      current_main_instance_id: "",
      current_map_path: "",
      open_resource_paths: [],
      selected_resource_tab: 0,
      selected_nav_top_tab: "current",
      edit_module_id: "",
      client_connection_ready: false,
      selected_tile_layer: "Terrain",
      current_map_index: 0,
      current_main_instance: {},
      selected_tile_position: { x: 0, y: 0 },
      selected_scene_props: {
        scene_path: null,
        transparency: 0.5,
        is_pinned: false,
      },
    });

    window.setInterval(() => {
      if (
        window?.medium?.communication_state?.is_connection_ready &&
        !state.client_connection_ready
      ) {
        state.client_connection_ready = true;
      } else if (
        !window?.medium?.communication_state?.is_connection_ready &&
        state.client_connection_ready
      ) {
        state.client_connection_ready = false;
      }
    }, 100);

    const getters = {
      current_main_instance_scene: computed(() => {
        const { game_instance_data_map } = storeToRefs(
          use_game_instances_store(),
        );
        const { instance_id, world_id } = state.current_main_instance;

        if (
          instance_id &&
          world_id &&
          game_instance_data_map.value[instance_id] &&
          game_instance_data_map.value[instance_id][world_id]
        ) {
          const game_instance_data =
            game_instance_data_map.value[instance_id][world_id];
          if (game_instance_data) {
            return game_instance_data.instance_scene;
          }
        }

        return null;
      }),
    };

    const actions = {
      toggle_sync_with_main_scene() {
        state.sync_with_main_scene = !state.sync_with_main_scene;
      },
      set_inspector_component(component: InspectorComponent) {
        state.active_component = component;
      },
      set_selected_tile_layer(layer: LayerKind) {
        state.selected_tile_layer = layer;
      },
      set_terrain_brush(brush: TerrainBrush | null, size: number) {
        state.terrain_brush = brush;
        state.terrain_brush_size = size;
        if (
          state.terrain_brush &&
          state.current_main_instance.instance_id &&
          state.current_main_instance.world_id
        ) {
          window.medium.adjust_brush_hover(
            state.current_main_instance.instance_id,
            state.current_main_instance.world_id,
            new Array(size).fill(new Array(size).fill(0, 0), 0),
          );
        }
      },
      set_tile_brush(brush: number[][]) {
        if (!brush.every((row) => row.every((v) => v === 0))) {
          this.set_terrain_brush(null, brush.length);
        }

        state.tile_brush = brush;
        if (
          state.current_main_instance.instance_id &&
          state.current_main_instance.world_id
        ) {
          window.medium.adjust_brush_hover(
            state.current_main_instance.instance_id,
            state.current_main_instance.world_id,
            brush,
          );
        }
      },
      set_selected_nav_top_tab(index: EditorStore["selected_nav_top_tab"]) {
        state.selected_nav_top_tab = index;
      },
      select_tile_position(tile_position: { x: number; y: number }) {
        state.selected_tile_position = tile_position;
      },
      get_node_to_select(mouse_event: SelectMouseEvent): RayCastNode | null {
        const { component_stores } = use_inspector_store();
        if (mouse_event.nodes.length === 0) {
          state.selection_data.node = null;
          return null;
        }
        console.log("nodes", mouse_event.nodes);
        let nodes_to_select: RayCastNode[] = [];

        if (state.edit_mode === "remove") {
          nodes_to_select = mouse_event.shiftKey
            ? actions.get_parent_ray_cast_nodes(mouse_event.nodes)
            : mouse_event.nodes.map((n) => ({
                game_node_id: n.instance_node_id ?? n.game_node_id,
                entity_id: n.entity_id,
                instance_node_id: n.instance_node_id,
              }));
        } else if (state.edit_mode === "select") {
          console.log("selecting", mouse_event.ctrlKey);
          nodes_to_select = nodes_to_select = mouse_event.ctrlKey
            ? mouse_event.nodes.map((n) => ({
                game_node_id: n.instance_node_id ?? n.game_node_id,
                entity_id: n.entity_id,
                instance_node_id: n.instance_node_id,
              }))
            : actions.get_parent_ray_cast_nodes(mouse_event.nodes);
        }
        const found_index = nodes_to_select.findIndex(
          (n) =>
            n.game_node_id ===
              component_stores.game_node.selected_game_node_id ||
            n.entity_id === component_stores.game_node.selected_entity_id,
        );
        console.log("n", nodes_to_select);
        return nodes_to_select[(found_index + 1) % nodes_to_select.length];
      },
      grid_left_click_start(mouse_event: SelectMouseEvent) {
        state.selection_data.node = actions.get_node_to_select(mouse_event);
        if (state.selection_data.node) {
          const game_node = actions.get_game_node_from_game_node_identifier(
            state.selection_data.node,
          );
          if (game_node?.Node2D.data.transform) {
            state.selection_data.node_position = {
              x: game_node.Node2D.data.transform.position[0],
              y: game_node.Node2D.data.transform.position[1],
            };
          }
        }
      },
      grid_left_click_end(mouse_event: SelectMouseEvent) {
        const node_to_select = actions.get_node_to_select(mouse_event);
        if (state.edit_mode === "select") {
          if (
            state.selection_data.node &&
            node_to_select &&
            state.selection_data.node.game_node_id ===
              node_to_select?.game_node_id
          ) {
            actions.select_node_from_ray_cast(node_to_select);
          }
        }

        if (state.edit_mode === "remove" && node_to_select) {
          if (node_to_select.entity_id !== null) {
            if (
              state.current_main_instance &&
              state.current_main_instance.instance_id &&
              state.current_main_instance.world_id
            ) {
              actions.remove_entity_server(
                state.selected_module_id,
                state.current_main_instance.instance_id,
                state.current_main_instance.world_id,
                node_to_select.entity_id,
              );
            }
          } else {
            const { blueprint_render } = use_game_instances_store();
            const { remove_child_from_scene_on_server } = use_resources_store();
            const game_node =
              actions.get_game_node_from_game_node_identifier(node_to_select);
            const path_from_blueprint = actions.get_path_from_blueprint(
              node_to_select.game_node_id,
            );
            if (
              blueprint_render?.scene_resource_path &&
              game_node &&
              path_from_blueprint !== null
            ) {
              remove_child_from_scene_on_server(
                blueprint_render.scene_resource_path,
                path_from_blueprint,
                game_node,
              );
            } else {
              console.error("Could not determine path from blueprint");
            }
          }
        }
        if (state.edit_mode === "placing") {
          const { component_stores } = use_inspector_store();
          const { tileset_map, get_or_load_tileset } = use_resources_store();
          const {
            tile_id,
            resource_path,
            layer,
            placement_type,
            node_to_place_in,
          } = component_stores.placing_config;
          if (resource_path !== null && node_to_place_in !== null) {
            console.log(node_to_place_in);
            const node_name =
              resource_path.split("/").pop()?.split(".")[0] ?? "new object";
            if (placement_type === "blueprint") {
              const instance_node = create_game_node2d(
                `${node_name.replace(/s$/, "")}`,
                {
                  Instance: resource_path,
                },
                {
                  position: [
                    mouse_event.simulation_coordinates.x,
                    mouse_event.simulation_coordinates.y,
                  ],
                },
              );
              actions.add_node_to_scene(
                "",
                node_to_place_in.game_node_id,
                node_to_place_in.entity_id,
                instance_node,
                node_to_place_in.entity_id !== null,
              );
              return;
            }

            if (tile_id === null) {
              return;
            }
            const tileset = get_or_load_tileset(tileset_map, resource_path);
            if (!tileset) {
              console.error(
                "Could not find tileset for adding node",
                resource_path,
              );
              return;
            }
            const tile = tileset.tiles[tile_id] as Tile | undefined;
            let node_to_add;
            const render_sprite = new_render_sprite();
            render_sprite.resource_path = resource_path;
            render_sprite.gid = tile_id;
            render_sprite.fade_in_effect = ["Inflate", 500];
            render_sprite.fade_out_effect = ["Deflate", 500];
            const render_node = create_game_node2d(
              `${node_name.replace(/s$/, "")} Prop`,
              {
                Render: {
                  offset: [0, 0],
                  layer,
                  kind: {
                    Sprite: render_sprite,
                  },
                },
              },
              {
                position: [
                  mouse_event.simulation_coordinates.x,
                  mouse_event.simulation_coordinates.y,
                ],
              },
            );
            if (placement_type === "background") {
              node_to_add = render_node;
            }
            if (placement_type === "object") {
              const [shape, shape_offset] =
                actions.get_collider_shape_from_tile(tile, tileset);
              render_node.Node2D.data.transform.position = shape_offset;
              render_node.Node2D.name = "Render";
              node_to_add = create_game_node2d(
                node_name.replace(/s$/, ""),
                {
                  RigidBody: {
                    body: "Fixed",
                    kinematic_character_controller_props: null,
                    dynamic_rigid_body_props: null,
                  },
                },
                {
                  position: [
                    mouse_event.simulation_coordinates.x,
                    mouse_event.simulation_coordinates.y,
                  ],
                },
              );
              const collider = create_game_node2d("Collider", {
                Collider: {
                  kind: "Solid",
                  restitution: 0.5,
                  density: 1.0,
                  shape,
                },
              });
              node_to_add.Node2D.children = [collider, render_node];
            }
            if (!node_to_add) {
              console.error("Could not determine node to add");
              return;
            }
            actions.add_node_to_scene(
              "",
              node_to_place_in.game_node_id,
              node_to_place_in.entity_id,
              node_to_add,
              node_to_place_in.entity_id !== null,
            );
          }
        }
      },
      get_collider_shape_from_tile(
        tile: Tile | undefined,
        tileset: Tileset,
      ): [ColliderShape, [number, number]] {
        const scale = get_simulation_scale();
        if (!tile) {
          if (!tileset.image) {
            console.error("Could not determine collision shape for tile", {
              tile,
              tileset,
            });
            return [{ Ball: 1 }, [0, 0]];
          }
          return [
            {
              Cuboid: [
                tileset.tile_width / scale / 2,
                tileset.tile_height / scale / 2,
              ],
            },
            [0, 0],
          ];
        }
        if (tile.collision_shape === null) {
          if (tile.image === null) {
            if (!tileset.image) {
              console.error("Could not determine collision shape for tile", {
                tile,
                tileset,
              });
              return [{ Ball: 1 }, [0, 0]];
            }
            return [
              {
                Cuboid: [
                  tileset.tile_width / scale / 2,
                  tileset.tile_height / scale / 2,
                ],
              },
              [0, 0],
            ];
          }

          return [
            {
              Cuboid: [
                tileset.tile_width / scale / 2,
                tileset.tile_height / scale / 2,
              ],
            },
            [0, 0],
          ];
        }

        return match(tile.collision_shape)
          .with(
            { Rectangle: P.select() },
            ([x0, y0, x1, y1]): [ColliderShape, [number, number]] => [
              {
                Cuboid: [x1 / scale / 2, y1 / scale / 2],
              },
              [-x0 / scale / 2, -y0 / scale / 2],
            ],
          )
          .with(
            { Polygon: P.select() },
            (_points): [ColliderShape, [number, number]] => [
              { Ball: 1 },
              [0, 0],
            ],
          )
          .with(
            { Circle: P.select() },
            ([_x, _y, r]): [ColliderShape, [number, number]] => [
              { Ball: r },
              [0, 0],
            ],
          )
          .exhaustive();
      },
      add_node_to_scene(
        _scene_resource: string,
        parent_game_node_id: string,
        parent_node_entity_id: Entity | null,
        node: GameNodeKind,
        is_from_current_instance: boolean,
      ) {
        if (is_from_current_instance) {
          if (
            state.selected_module_id &&
            state.current_main_instance?.instance_id &&
            state.current_main_instance?.world_id &&
            typeof parent_node_entity_id === "number"
          ) {
            actions.add_entity_server(
              state.selected_module_id,
              state.current_main_instance.instance_id,
              state.current_main_instance.world_id,
              parent_node_entity_id,
              node,
            );
          } else {
            console.error(
              "Could not attempt to add entity on server... weird.",
            );
          }
        } else {
          const { blueprint_render } = use_game_instances_store();
          const { add_child_to_scene_on_server } = use_resources_store();
          const path_from_blueprint =
            actions.get_path_from_blueprint(parent_game_node_id);

          if (
            blueprint_render?.scene_resource_path &&
            path_from_blueprint !== null
          ) {
            add_child_to_scene_on_server(
              blueprint_render.scene_resource_path,
              path_from_blueprint,
              parent_game_node_id,
              node,
            );
          } else {
            console.error(
              "Could not determine path from blueprint or blueprint render does not exist",
            );
          }
        }
      },
      grid_mouse_move(mouse_event: SelectMouseEvent) {
        if (state.selection_data.node && mouse_event.start) {
          const [diff_x, diff_y] = [
            mouse_event.mouseX - mouse_event.start.click_start.x,
            mouse_event.mouseY - mouse_event.start.click_start.y,
          ];
          const scale = get_simulation_scale();
          if (state.selection_data.node_position) {
            const [new_x, new_y] = [
              state.selection_data.node_position.x +
                (diff_x * mouse_event.zoom) / scale,
              state.selection_data.node_position.y +
                (diff_y * mouse_event.zoom) / scale,
            ];
            if (state.selection_data.node.entity_id !== null) {
              actions.update_ray_cast_node(state.selection_data.node, {
                PositionRotation: [new_x, new_y, 0],
              });
            } else {
              const game_node = actions.get_game_node_from_game_node_identifier(
                state.selection_data.node,
              );

              if (game_node) {
                const transform = game_node.Node2D.data.transform;
                actions.update_ray_cast_node(state.selection_data.node, {
                  Transform: { ...transform, position: [new_x, new_y] },
                });
              }
            }
          }
        }
      },
      update_ray_cast_node(
        ray_cast_node: RayCastNode,
        entity_update: EntityUpdateKind,
      ) {
        const { update_data_in_scene_node_on_server, update_instance_node } =
          use_resources_store();
        if (state.sync_with_main_scene) {
          console.log(ray_cast_node);
        }
        if (ray_cast_node.entity_id === null) {
          const { blueprint_render } = use_game_instances_store();
          const blueprint_path = actions.get_path_from_blueprint(
            ray_cast_node.game_node_id,
          );
          if (blueprint_render && blueprint_path !== null) {
            update_data_in_scene_node_on_server(
              blueprint_render.scene_resource_path,
              blueprint_path,
              ray_cast_node.game_node_id,
              entity_update,
            );
          } else {
            console.error(
              "Could not determine path from blueprint or blueprint render does not exist",
            );
          }
        } else if (
          state.selected_module_id &&
          state.current_main_instance &&
          state.current_main_instance.instance_id !== undefined &&
          state.current_main_instance.world_id !== undefined
        ) {
          update_instance_node(
            state.selected_module_id,
            state.current_main_instance.instance_id,
            state.current_main_instance.world_id,
            {
              id: ray_cast_node.entity_id,
              kind: entity_update,
            },
          );
        }
      },
      select_node_from_ray_cast(node: RayCastNode) {
        const { select_game_node } = use_inspector_store();
        const { blueprint_render } = use_game_instances_store();
        const current_main_instance_scene =
          getters.current_main_instance_scene.value;

        if (node.entity_id !== null && current_main_instance_scene) {
          select_game_node(
            current_main_instance_scene.resource_path,
            node.game_node_id,
            true,
            node.entity_id,
          );
        } else if (blueprint_render?.scene_resource_path) {
          select_game_node(
            blueprint_render.scene_resource_path,
            node.game_node_id,
            false,
            null,
          );
        }
      },
      get_game_node_from_game_node_identifier(
        node: GameNodeIdentifier,
      ): GameNodeKind | null {
        const render_graph_data =
          actions.get_render_graph_data_for_game_node_identifier(node);
        if (render_graph_data) {
          return (
            render_graph_data.entity_node_map[
              node.entity_id ?? node.game_node_id
            ] ?? null
          );
        }
        return null;
      },
      get_render_graph_data_for_game_node_identifier(
        node: GameNodeIdentifier,
      ): RenderGraphData | null {
        const { game_instance_data_map, blueprint_render } =
          use_game_instances_store();
        if (node.entity_id !== null) {
          if (
            state.current_main_instance &&
            state.current_main_instance.instance_id &&
            state.current_main_instance.world_id &&
            game_instance_data_map[state.current_main_instance.instance_id] &&
            game_instance_data_map[state.current_main_instance.instance_id][
              state.current_main_instance.world_id
            ]
          ) {
            return (
              game_instance_data_map[state.current_main_instance.instance_id][
                state.current_main_instance.world_id
              ]?.render_graph_data ?? null
            );
          }
        }

        return (blueprint_render?.render_graph_data as RenderGraphData) ?? null;
      },
      get_parent_ray_cast_nodes(nodes: RayCastNode[]): RayCastNode[] {
        const parent_nodes = nodes.map((n) => {
          const instance_node = actions.get_ray_cast_node_instance(n);
          if (instance_node) {
            return instance_node;
          }
          return actions.get_parent_ray_cast_node(n);
        });
        return parent_nodes.filter((n) => n !== null) as RayCastNode[];
      },
      get_parent_ray_cast_node(n: RayCastNode): RayCastNode | null {
        const render_graph_data =
          actions.get_render_graph_data_for_game_node_identifier(n);
        if (render_graph_data === null) {
          return null;
        }
        const parent =
          render_graph_data.entity_node_to_render_node_map[
            n.entity_id ?? n.game_node_id
          ]?.parent;
        if (parent) {
          const game_node = render_graph_data.entity_node_map[parent.node_id];
          if (game_node) {
            return {
              game_node_id: game_node.Node2D.id,
              entity_id: game_node.Node2D.entity_id,
            };
          }
        }
        return null;
      },
      get_ray_cast_node_instance(node: RayCastNode): RayCastNode | null {
        if (node.instance_node_id) {
          return {
            game_node_id: node.instance_node_id,
            entity_id: null,
            instance_node_id: node.instance_node_id,
          };
        }
        return null;
      },
      set_camera_position(
        instance_id: string,
        world_id: string,
        iso: Isometry,
      ) {
        window.medium.set_camera_iso(instance_id, world_id, iso);
      },
      set_camera_zoom(instance_id: string, world_id: string, zoom: number) {
        window.medium.set_camera_zoom(instance_id, world_id, zoom);
      },
      get_camera_position(instance_id: string, world_id: string): Isometry {
        return window.medium.get_camera_iso(instance_id, world_id);
      },
      get_camera_zoom(instance_id: string, world_id: string): number {
        return window.medium.get_camera_zoom(instance_id, world_id);
      },
      set_game_instance_map(instance_data: [string, string[]][]) {
        const module_instance_map: EditorStore["module_instance_map"] = {};
        for (const d of instance_data) {
          module_instance_map[d[0]] = d[1];
        }
        state.module_instance_map = module_instance_map;
      },
      set_current_main_instance(instance_id: string, world_id: string) {
        state.current_main_instance = { instance_id, world_id };

        window.medium.swap_main_render_instance(instance_id, world_id);
      },
      set_selected_scene(scene_path: string) {
        state.selected_scene_props.scene_path = scene_path;
      },
      add_module_instance(module_id: string, game_instance_id: string) {
        state.module_instance_map = {
          ...state.module_instance_map,
          [module_id]: state.module_instance_map[module_id]
            ? [...state.module_instance_map[module_id], game_instance_id]
            : [],
        };
      },
      remove_module_instance(module_id: string, game_instance_id: string) {
        state.module_instance_map = {
          ...state.module_instance_map,
          [module_id]: state.module_instance_map[module_id]
            ? state.module_instance_map[module_id].filter(
                (g) => g != game_instance_id,
              )
            : [],
        };
      },
      set_selected_tile(tileset_path: string, tile_id: number) {
        state.selected_tileset_path = tileset_path;
        state.selected_tile_id = tile_id;
      },
      set_selected_resource_tab(index: number) {
        state.selected_resource_tab = index;
      },
      close_resource(path: string) {
        state.open_resource_paths = state.open_resource_paths.filter(
          (p) => p !== path,
        );
      },
      add_open_resource_path(path: string): number {
        if (state.open_resource_paths.includes(path)) {
          return state.open_resource_paths.findIndex((p) => p === path);
        }
        state.open_resource_paths = [...state.open_resource_paths, path];
        return state.open_resource_paths.length - 1;
      },
      set_selected_module_id(id: string) {
        state.selected_module_id = id;
      },
      set_current_main_instance_id(id: string) {
        state.current_main_instance_id = id;
      },

      open_game_instance_server(module_id: string) {
        send_admin_event({ OpenInstance: module_id });
      },
      remove_entity_server(
        module_id: string,
        game_instance_id: string,
        world_id: string,
        entity: Entity,
      ) {
        send_admin_event({
          RemoveInstanceNode: [module_id, game_instance_id, world_id, entity],
        });
      },
      add_entity_server(
        module_id: string,
        game_instance_id: string,
        world_id: string,
        parent_entity_id: Entity,
        game_node: GameNodeKind,
      ) {
        send_admin_event({
          AddNodeToInstanceNode: [
            module_id,
            game_instance_id,
            world_id,
            parent_entity_id,
            game_node,
          ],
        });
      },
      start_inspecting_world(
        module_id: string,
        game_instance_id: string,
        world_id: string,
        map_resource_path: string,
      ) {
        send_admin_event({
          StartInspectingWorld: [module_id, game_instance_id, world_id],
        });
        state.inspecting_worlds.main = {
          module_id,
          instance_id: game_instance_id,
          world_id,
          map_resource_path,
        };
      },
      stop_inspecting_world(
        module_id: string,
        game_instance_id: string,
        world_id: string,
      ) {
        send_admin_event({
          StopInspectingWorld: [module_id, game_instance_id, world_id],
        });
        delete state.inspecting_worlds.main;
      },
      reset_world(module_id: string, instance_id: string, world_id: string) {
        send_admin_event({
          ResetGameWorld: [module_id, instance_id, world_id],
        });
      },
      show_editor() {
        state.editor_open = true;
      },
      hide_editor() {
        state.editor_open = false;
      },
      get_path_from_blueprint(node_id: string): number[] | null {
        const { blueprint_render } = use_game_instances_store();

        if (
          blueprint_render?.render_graph_data?.entity_node_to_render_node_map
        ) {
          const render_graph_data = blueprint_render.render_graph_data;
          const render_node =
            render_graph_data.entity_node_to_render_node_map[node_id];
          if (!render_node) {
            console.error("Could not find render node for node", node_id);
            return null;
          }
          return _get_path_from_render_graph_data(
            render_node,
            render_graph_data as unknown as RenderGraphData,
            [],
          );
        }

        return null;
      },
    };

    function _get_path_from_render_graph_data(
      node: Node,
      render_graph_data: RenderGraphData,
      path: number[],
    ): number[] {
      if (render_graph_data.entity_node_to_render_node_map) {
        const parent =
          render_graph_data.entity_node_to_render_node_map[node.node_id].parent;
        if (parent) {
          const index = parent.children.findIndex(
            (n) => n.node_id === node.node_id,
          );
          if (index !== -1) {
            return _get_path_from_render_graph_data(parent, render_graph_data, [
              index,
              ...path,
            ]);
          }
        }
      }

      return path;
    }

    const { get_or_load_scene } = use_resources_store();
    const { scene_map } = toRefs(use_resources_store());
    watch(
      [state.selected_scene_props, scene_map, state.client_connection_ready],
      () => {
        if (!state.client_connection_ready) {
          return;
        }
        const scene_path = state.selected_scene_props.scene_path;
        if (!scene_path) {
          return;
        }
        const scene = get_or_load_scene(scene_map.value, scene_path);
        if (!scene) {
          return;
        }
        const { set_and_render_blueprint_render } = use_game_instances_store();
        if (
          state.inspecting_worlds?.main?.instance_id &&
          state.inspecting_worlds?.main?.world_id &&
          window.medium.is_instance_ready(
            state.inspecting_worlds.main.instance_id,
            state.inspecting_worlds.main.world_id,
          )
        ) {
          set_and_render_blueprint_render(
            state.selected_module_id,
            scene_path,
            scene,
            state.selected_scene_props.is_pinned,
          );
        }
      },
    );

    return {
      ...toRefs(state),
      ...getters,
      ...actions,
    };
  },
  {
    persist: {
      afterRestore: (ctx) => {
        ctx.store.$state.editor_open = false;
      },
    },
  },
);
function send_admin_event(event: AdminToSystemEvent) {
  if (window.medium.communication_state.is_connection_open) {
    window.medium.communication_state.ws_connection.send(JSON.stringify(event));
  }
}

export function audio_set_key(audio_set: AudioSet) {
  return `${audio_set.resource_path}/${audio_set.name}.audio.json`;
}

export function tileset_key(tileset: Tileset) {
  return `${tileset.resource_path}/${tileset.name}.tileset.json`;
}

export function resource_key(resource: BlueprintResource) {
  return `${resource.dir}/${resource.file_name}`;
}

export function map_key(game_map: { resource_path: string; name: string }) {
  return `${game_map.resource_path}/${game_map.name}.map.json`;
}
